import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { VoiceAiMessageMetadata, VoiceAiMessageRole } from '@pleased-npm/voice-ai-client';
import { VoiceAiMessageKind, VoiceAiRole, VoiceTranscriptStatus } from '@/common/voice-ai/voiceAiMessage';

/**
 * Live transcript of the landing Voice AI call (@pleased-npm/voice-ai-client).
 *
 * The host receives a flat stream of onMessage events and this
 * store turns it into renderable rows. Key behaviours:
 *  - AI text is revealed letter-by-letter from a queue, paced to match the
 *    audio playback rate instead of LLM speed.
 *  - When the AI is interrupted, the row is flagged `interrupted` and the
 *    remaining buffered text is flushed at once (no visual distinction).
 *  - A "thinking" placeholder bridges the gap before the AI audio starts, and
 *    a "listening" placeholder bridges user speech before transcription lands.
 */

export type TranscriptRole = VoiceAiMessageRole;

export type AiUserStatus = VoiceTranscriptStatus;

export interface AiUserMessage {
  id: string;
  role: 'user' | 'ai';
  status: AiUserStatus;
  /** Revealed transcript text for this row. */
  spokenText: string;
  /** OpenAI conversation item id — ties a user row to one speech turn. */
  itemId?: string | null;
}

export type TranscriptMessage = AiUserMessage;

interface RowRuntime {
  pending: string;
  audioStarted: boolean;
  audioStopped: boolean;
  isInterrupted: boolean;
  revealTimer: number;
}

// Reveal pacing. Deltas arrive at LLM speed; we drain them slowly so the text
// appears in sync with the spoken audio, with adaptive catch-up on backlog.
const REVEAL_TICK_MS = 70; // ~14 chars/s ≈ 170 wpm
const REVEAL_BACKLOG_MID = 80; // queued chars → reveal 2/tick
const REVEAL_BACKLOG_HIGH = 160; // queued chars → reveal 4/tick

export const useVoiceTranscript = defineStore('voiceTranscript', () => {
  const messages = ref<TranscriptMessage[]>([]);
  // Timestamp of when the call ended (set in `finalize`). Acts as the timeline
  // anchor for the transcript block: conversation messages created before this
  // moment render above the transcript, messages created after render below it.
  const endedAt = ref<number | null>(null);
  const aiIdByResponseId = new Map<string, string>();
  const userIdByItemId = new Map<string, string>();
  const runtimeById = new Map<string, RowRuntime>();
  let userListeningId: string | null = null;
  let pendingThinkingId: string | null = null;
  let idCounter = 0;

  const nextId = () => `vt-${Date.now()}-${(idCounter += 1)}`;

  const findIndex = (id: string) => messages.value.findIndex((m) => m.id === id);

  const getMessageById = (id: string | null): AiUserMessage | null => {
    if (!id) return null;
    return messages.value.find((m) => m.id === id) ?? null;
  };

  const getUserRowByItemId = (itemId: string | null | undefined): AiUserMessage | null => {
    if (!itemId) return null;
    const rowId = userIdByItemId.get(itemId);
    return rowId ? getMessageById(rowId) : null;
  };

  const bindUserItemId = (message: AiUserMessage, itemId: string | null | undefined) => {
    if (!itemId) return;
    if (message.itemId && message.itemId !== itemId) {
      userIdByItemId.delete(message.itemId);
    }
    message.itemId = itemId;
    userIdByItemId.set(itemId, message.id);
  };

  const unbindUserItemId = (message: AiUserMessage) => {
    if (!message.itemId) return;
    userIdByItemId.delete(message.itemId);
  };

  const removeRow = (id: string) => {
    const index = findIndex(id);
    if (index !== -1) messages.value.splice(index, 1);
    stopRevealLoop(id);
    runtimeById.delete(id);
  };

  // ----- reveal queue ---------------------------------------------------
  const appendRevealed = (id: string, chunk: string) => {
    const message = getMessageById(id);
    const runtime = runtimeById.get(id);
    if (!message || !runtime) return;
    message.spokenText += chunk;
  };

  const revealTick = (id: string) => {
    const runtime = runtimeById.get(id);
    if (!runtime) return;

    if (!runtime.pending) {
      if (runtime.audioStopped) stopRevealLoop(id);
      return;
    }

    if (!runtime.audioStarted && !runtime.audioStopped && !runtime.isInterrupted) {
      return;
    }

    let revealCount = 1;
    if (runtime.audioStopped || runtime.isInterrupted) {
      revealCount = runtime.pending.length;
    } else if (runtime.pending.length > REVEAL_BACKLOG_HIGH) {
      revealCount = 4;
    } else if (runtime.pending.length > REVEAL_BACKLOG_MID) {
      revealCount = 2;
    }

    const chunk = runtime.pending.slice(0, revealCount);
    runtime.pending = runtime.pending.slice(revealCount);
    appendRevealed(id, chunk);
  };

  const startRevealLoop = (id: string) => {
    const runtime = runtimeById.get(id);
    if (!runtime || runtime.revealTimer) return;
    runtime.revealTimer = window.setInterval(() => revealTick(id), REVEAL_TICK_MS);
  };

  function stopRevealLoop(id: string) {
    const runtime = runtimeById.get(id);
    if (runtime?.revealTimer) {
      clearInterval(runtime.revealTimer);
      runtime.revealTimer = 0;
    }
  }

  const flushRevealNow = (id: string) => {
    const runtime = runtimeById.get(id);
    if (!runtime) return;
    if (runtime.pending) {
      appendRevealed(id, runtime.pending);
      runtime.pending = '';
    }
    stopRevealLoop(id);
  };

  // ----- thinking placeholder ------------------------------------------
  const dropPendingThinking = () => {
    if (!pendingThinkingId) return;
    removeRow(pendingThinkingId);
    pendingThinkingId = null;
  };

  // A response is still "active" while it is streaming or has buffered text
  // left to reveal. We must not spawn a new thinking placeholder during that
  // window — its deltas would land in the existing row, orphaning the bubble.
  const hasActiveResponse = () =>
    messages.value.some((m) => {
      if (m.role !== 'ai') return false;
      if (m.status === VoiceTranscriptStatus.Streaming) return true;
      const runtime = runtimeById.get(m.id);
      return !!runtime && runtime.pending.length > 0;
    });

  const createAiRow = (status: AiUserStatus): AiUserMessage => {
    const message: AiUserMessage = {
      id: nextId(),
      role: 'ai',
      status,
      spokenText: '',
    };
    messages.value.push(message);
    runtimeById.set(message.id, {
      pending: '',
      audioStarted: false,
      audioStopped: false,
      isInterrupted: false,
      revealTimer: 0,
    });
    return message;
  };

  // On the first delta we adopt the pending "thinking" row if present,
  // repurposing it as the live bubble for this responseId.
  const ensureAiEntry = (responseId: string): AiUserMessage => {
    const existingId = aiIdByResponseId.get(responseId);
    const existing = getMessageById(existingId ?? null);
    if (existing) {
      dropPendingThinking();
      return existing;
    }

    let message: AiUserMessage;
    if (pendingThinkingId) {
      const adoptedId = pendingThinkingId;
      pendingThinkingId = null;
      const adopted = getMessageById(adoptedId);
      if (adopted) {
        adopted.status = VoiceTranscriptStatus.Streaming;
        message = adopted;
      } else {
        message = createAiRow(VoiceTranscriptStatus.Streaming);
      }
    } else {
      message = createAiRow(VoiceTranscriptStatus.Streaming);
    }
    aiIdByResponseId.set(responseId, message.id);
    startRevealLoop(message.id);
    return message;
  };

  // ----- user turn handlers ---------------------------------------------
  const cancelListeningPlaceholder = (
    itemId: string | null | undefined,
    { force = false }: { force?: boolean } = {}
  ) => {
    if (!userListeningId) return;

    const listening = getMessageById(userListeningId);
    if (!listening) {
      userListeningId = null;
      return;
    }

    if (!force && itemId && listening.itemId && itemId !== listening.itemId) {
      return;
    }

    unbindUserItemId(listening);
    removeRow(listening.id);
    userListeningId = null;
  };

  const retractUserRow = (itemId: string | null | undefined) => {
    if (!itemId) return;

    const rowId = userIdByItemId.get(itemId);
    if (!rowId) return;

    if (userListeningId === rowId) {
      userListeningId = null;
    }

    userIdByItemId.delete(itemId);
    removeRow(rowId);
  };

  const commitUserTranscript = (text: string, itemId: string | null | undefined, listening: AiUserMessage | null) => {
    if (listening) {
      listening.spokenText = text;
      listening.status = VoiceTranscriptStatus.Done;
      bindUserItemId(listening, itemId);
      userListeningId = null;
      return;
    }

    const existing = getUserRowByItemId(itemId);
    if (existing) {
      if (existing.spokenText === text) return;
      existing.spokenText = text;
      existing.status = VoiceTranscriptStatus.Done;
      return;
    }

    const message: AiUserMessage = {
      id: nextId(),
      role: 'user',
      status: VoiceTranscriptStatus.Done,
      spokenText: text,
    };
    bindUserItemId(message, itemId);

    const activeListening = getMessageById(userListeningId);
    if (activeListening && itemId && activeListening.itemId && itemId !== activeListening.itemId) {
      const listeningIndex = findIndex(activeListening.id);
      if (listeningIndex !== -1) {
        messages.value.splice(listeningIndex, 0, message);
        return;
      }
    }

    messages.value.push(message);
  };

  const handleUserMessage = (text: string, metadata?: VoiceAiMessageMetadata) => {
    const kind = metadata?.kind;
    const itemId = metadata && 'itemId' in metadata ? (metadata.itemId ?? null) : undefined;

    if (kind === VoiceAiMessageKind.Listening) {
      if (userListeningId) {
        const listening = getMessageById(userListeningId);
        if (listening) {
          bindUserItemId(listening, itemId);
        }
        return;
      }

      const message: AiUserMessage = {
        id: nextId(),
        role: 'user',
        status: VoiceTranscriptStatus.Listening,
        spokenText: '',
        itemId: itemId ?? null,
      };
      messages.value.push(message);
      bindUserItemId(message, itemId);
      userListeningId = message.id;
      return;
    }

    if (kind === VoiceAiMessageKind.ListeningCancel || kind === VoiceAiMessageKind.UserDrop) {
      const force = kind === VoiceAiMessageKind.ListeningCancel && metadata?.force === true;
      cancelListeningPlaceholder(itemId, { force });
      return;
    }

    if (kind === VoiceAiMessageKind.UserRetract) {
      retractUserRow(itemId);
      return;
    }

    // Subtitle ledger staging — visible text arrives on the later final event.
    if (kind === VoiceAiMessageKind.FinalPending) {
      return;
    }

    if (!text) return;

    const listeningRow = getMessageById(userListeningId);
    const listening = listeningRow && (!listeningRow.itemId || listeningRow.itemId === itemId) ? listeningRow : null;

    commitUserTranscript(text, itemId, listening);
  };

  const handleThinkingStart = () => {
    if (pendingThinkingId) return;
    if (hasActiveResponse()) return;

    const message = createAiRow(VoiceTranscriptStatus.Thinking);
    pendingThinkingId = message.id;
  };

  const handleAiMessage = (text: string, metadata?: VoiceAiMessageMetadata) => {
    const kind = metadata?.kind ?? VoiceAiMessageKind.Final;

    if (kind === VoiceAiMessageKind.ThinkingStart) {
      handleThinkingStart();
      return;
    }

    if (kind === VoiceAiMessageKind.Delta) {
      const { responseId } = metadata as Extract<VoiceAiMessageMetadata, { kind: typeof VoiceAiMessageKind.Delta }>;
      if (!responseId) return;
      const message = ensureAiEntry(responseId);
      const runtime = runtimeById.get(message.id);
      if (!runtime) return;
      runtime.pending += text;
      startRevealLoop(message.id);
      return;
    }

    if (kind === VoiceAiMessageKind.AudioStarted) {
      const { responseId } = metadata as Extract<
        VoiceAiMessageMetadata,
        { kind: typeof VoiceAiMessageKind.AudioStarted }
      >;
      const id = aiIdByResponseId.get(responseId);
      const runtime = id ? runtimeById.get(id) : undefined;
      if (!id || !runtime) return;
      runtime.audioStarted = true;
      startRevealLoop(id);
      return;
    }

    if (kind === VoiceAiMessageKind.AudioStopped) {
      const { responseId } = metadata as Extract<
        VoiceAiMessageMetadata,
        { kind: typeof VoiceAiMessageKind.AudioStopped }
      >;
      const id = aiIdByResponseId.get(responseId);
      const message = getMessageById(id ?? null);
      const runtime = id ? runtimeById.get(id) : undefined;
      if (!id || !message || !runtime) return;
      runtime.audioStopped = true;
      flushRevealNow(id);

      if (!message.spokenText) {
        aiIdByResponseId.delete(responseId);
        removeRow(id);
        return;
      }
      message.status = VoiceTranscriptStatus.Done;
      return;
    }

    if (kind === VoiceAiMessageKind.Interrupted) {
      const { responseId } = metadata as Extract<
        VoiceAiMessageMetadata,
        { kind: typeof VoiceAiMessageKind.Interrupted }
      >;
      const id = aiIdByResponseId.get(responseId);
      const message = getMessageById(id ?? null);
      const runtime = id ? runtimeById.get(id) : undefined;
      if (!id || !message || !runtime) return;
      runtime.isInterrupted = true;
      flushRevealNow(id);

      if (!message.spokenText) {
        aiIdByResponseId.delete(responseId);
        removeRow(id);
        return;
      }
      message.status = VoiceTranscriptStatus.Interrupted;
      return;
    }

    // kind === VoiceAiMessageKind.Final — text generation done; let the reveal
    // queue keep draining at speech rate. audio_stopped finalizes the row.
    const { responseId } = (metadata ?? {}) as { responseId?: string };
    const existingId = responseId ? aiIdByResponseId.get(responseId) : undefined;
    const existing = getMessageById(existingId ?? null);
    if (existing) {
      const runtime = runtimeById.get(existing.id);

      if (!existing.spokenText && !runtime?.pending && text) {
        existing.spokenText = text;
      }
      return;
    }

    if (text) {
      const message = createAiRow(VoiceTranscriptStatus.Done);
      message.spokenText = text;
    }
  };

  /** Entry point wired into the voice client `onMessage` callback. */
  const handleMessage = (text: string, who: TranscriptRole, metadata?: VoiceAiMessageMetadata) => {
    // RAG/system events are intentionally not surfaced in the transcript.
    if (who === VoiceAiRole.System) return;
    if (who === VoiceAiRole.User) {
      handleUserMessage(text, metadata);
      return;
    }
    handleAiMessage(text, metadata);
  };

  const finalize = () => {
    userListeningId = null;
    dropPendingThinking();

    for (const message of [...messages.value]) {
      if (message.role !== 'ai') {
        if (message.status === VoiceTranscriptStatus.Listening) {
          unbindUserItemId(message);
          removeRow(message.id);
        }
        continue;
      }

      const runtime = runtimeById.get(message.id);
      if (runtime?.pending) flushRevealNow(message.id);
      stopRevealLoop(message.id);

      if (!message.spokenText) {
        removeRow(message.id);
        continue;
      }

      if (message.status === VoiceTranscriptStatus.Streaming || message.status === VoiceTranscriptStatus.Thinking) {
        message.status = VoiceTranscriptStatus.Done;
      }
    }

    aiIdByResponseId.clear();
    endedAt.value = Date.now();
  };

  const reset = () => {
    runtimeById.forEach((_, id) => stopRevealLoop(id));
    runtimeById.clear();
    aiIdByResponseId.clear();
    userIdByItemId.clear();
    userListeningId = null;
    pendingThinkingId = null;
    messages.value = [];
    endedAt.value = null;
  };

  return {
    messages,
    endedAt,
    handleMessage,
    finalize,
    reset,
  };
});
